import { Box, Flex, Text } from '@chakra-ui/react'
import React from 'react'
import { Link } from 'components/Link'
import { Seo } from 'components/Seo'
import { useThemeColors } from 'hooks/useThemeColors'
import { BaseLayout } from 'layouts/BaseLayout'

const PARENT_HOVER_STYLES = {
  '.flex-box:hover .link': {
    display: 'block',
  },
}

type SectionProps = {
  id: string
  title: string
  text: string
}

const Section = ({ id, title, text }: SectionProps) => {
  const COLORS = useThemeColors()

  return (
    <Box __css={PARENT_HOVER_STYLES}>
      <Flex className="flex-box" id={id} alignItems="start">
        <Text variant="title2bold">{title}</Text>
        <Box flex={1} pos="relative">
          <Link
            className="link"
            ml={2}
            color={COLORS.grey03}
            href={`#${id}`}
            pos="absolute"
            left="0"
            top={0.5}
            display="none"
            _hover={{ cursor: 'pointer', color: COLORS.zircuitLight }}
          >
            #
          </Link>
        </Box>
      </Flex>
      <Text mt={4} color={COLORS.grey01} variant="text1regular">
        {text}
      </Text>
    </Box>
  )
}

const FAQ = () => (
  <>
    <Seo title="FAQ" />
    <Flex gap={10} flexDir="column">
      <Section
        id="bridge-transaction"
        title="What is a bridge transaction?"
        text="A bridge transaction is a cross-chain transaction that requires a bridge component to manage it. Zircuit Garfield Testnet bridge transactions lock up assets like ETH or ERC-20 tokens on one chain and mint corresponding assets on another chain. A deposit transaction is a bridge transaction where the source chain is Ethereum and the target chain is Zircuit Garfield Testnet, while a withdrawal transaction is one where the source chain is Zircuit Garfield Testnet and the target chain is Ethereum."
      />
      <Section
        id="cross-domain-message"
        title="What is a cross domain message?"
        text="A cross domain message is where data is relayed across chains. Typically a cross domain message would contain information representing a function call on a contract on the other chain—for example, an ERC-20 token transfer."
      />
      <Section
        id="send-cross-domain-message"
        title="What does it mean to send a cross domain message?"
        text="Sending a cross domain message is the act of sending the message to the cross domain messaging contract on the source chain—for example, from Ethereum to Zircuit Garfield Testnet."
      />
      <Section
        id="relay-cross-domain-message"
        title="What does it mean to relay a cross domain message?"
        text="Relaying a cross domain message is the act of taking a message that was already sent on the source chain and applying it to the target chain in the form of a transaction. This is the claiming step of cross domain messaging."
      />
      <Section
        id="replay-cross-domain-message"
        title="What does it mean to replay a cross domain message?"
        text="Replaying a cross domain message is the act of re-sending an existing Ethereum to Zircuit Garfield Testnet message with a different gas limit to the cross domain messaging contract on Ethereum. Replaying is not applicable to the Zircuit Garfield Testnet to Ethereum direction."
      />
      <Section
        id="bridge-onto-zircuit-testnet"
        title="How do I bridge funds onto Zircuit Garfield Testnet?"
        text="To bridge funds to Zircuit Garfield Testnet, connect your wallet and follow the corresponding prompts. Select available L1 as the “from” network and Zircuit Garfield Testnet as the “to” network. Next, insert the amount of ETH you want to transfer. Click on “Bridge” and complete the transaction with your wallet."
      />
      <Section
        id="bridge-off-of-zircuit-testnet"
        title="How do I bridge funds off of Zircuit Garfield Testnet?"
        text="To bridge funds off Zircuit Testnet, connect your wallet and follow the corresponding prompts. Select Zircuit Testnet as the “from” network and available L1 as the “to” network. Next, insert the amount of ETH you want to withdraw. Then, click on “Bridge” and complete the transaction using your wallet. This will initiate the withdrawal and a transaction will appear on the “Transactions” page. In a few moments, the transaction’s status will change to “Ready to Prove” --- click on the status and complete the prompted (Ethereum) transaction by clicking “Execute”. Shortly afterwards, the status should change to “Ready to Relay”. Complete this (Ethereum) transaction to finish your withdrawal transaction."
      />
      <Section
        id="prove-withdrawal"
        title="Why do I have to prove my withdrawal?"
        text="A withdrawal proof demonstrates that the transaction initiating the withdrawal transaction is included in an L2 state root which was accompanied by a correct validity proof and recorded on Ethereum. These proofs are sometimes called inclusion proofs as they show the withdrawal was included in some valid state root."
      />
      <Section
        id="claim-withdrawal"
        title="Why do I have to claim my withdrawal?"
        text="Claiming is necessary to ultimately pull the funds from the L1 vault for Zircuit Testnet into your account (or send it to its destination account), once the system is convinced that you are entitled to funds via the prior inclusion proof. We plan to remove the two stage withdrawal in the future in order to simplify the withdrawal process."
      />
    </Flex>
  </>
)

FAQ.getLayout = (page: React.ReactNode) => <BaseLayout>{page}</BaseLayout>

export default FAQ
